import type { ReactNode } from "react";
import { ChevronRight, Target } from "lucide-react";
import type { SavingGoal } from "../../../types";
import type { View } from "../../../app/views";
import { money } from "../../../lib/finance";
import { goalCurrentSaved } from "../../../lib/goals";
import { Private } from "../../../components/Privacy";

export default function GoalsModule({
  goals,
  today,
  baseCurrency = "MVR",
  collapsed,
  collapseButton,
  nav,
}: {
  goals: SavingGoal[];
  today: string;
  baseCurrency?: string;
  collapsed: boolean;
  collapseButton: ReactNode;
  nav: (view: View) => void;
}) {
  const activeGoals = goals.filter((g) => g.status === "active");
  if (!activeGoals.length) return null;

  const currentMonth = today.slice(0, 7);
  const totalSaved = activeGoals.reduce((sum, g) => sum + goalCurrentSaved(g, currentMonth), 0);
  const totalTarget = activeGoals.reduce((sum, g) => sum + g.targetAmount, 0);
  const overallPct = totalTarget > 0 ? Math.min(100, Math.round((totalSaved / totalTarget) * 100)) : 0;

  return (
    <section className={`position-card overview-module${collapsed ? " is-collapsed" : ""}`}>
      <div className="section-heading">
        <h2>Savings goals</h2>
        <div className="overview-heading-actions">
          <strong className="collapsed-summary">
            {overallPct}% of {money(totalTarget, baseCurrency)}
          </strong>
          <Target size={19} />
          {collapseButton}
        </div>
      </div>
      {activeGoals.slice(0, 3).map((goal) => {
        const saved = goalCurrentSaved(goal, currentMonth);
        const pct = goal.targetAmount > 0 ? Math.min(100, Math.round((saved / goal.targetAmount) * 100)) : 0;
        const dotColor = "mint";
        return (
          <div className="position-line" key={goal.id}>
            <span>
              <i className={`dot ${dotColor}`} />
              {goal.name}
              {goal.floatingGroup && <small className="muted"> · pool</small>}
            </span>
            <strong>
              <Private>{pct}%</Private>
              <small className="muted" style={{ marginLeft: "0.4rem", fontWeight: 400 }}>
                ({money(saved, goal.currency)})
              </small>
            </strong>
          </div>
        );
      })}
      <button className="net-position owed-link" onClick={() => nav("goals")}>
        <span>
          {activeGoals.length} active {activeGoals.length === 1 ? "goal" : "goals"}
          <small>
            <Private>{money(totalSaved, baseCurrency)}</Private> reserved from safe-to-spend
          </small>
        </span>
        <ChevronRight size={18} />
      </button>
    </section>
  );
}
